import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Product } from '../data/products';
import { useProductsStore } from '../store/productsStore';
import ProductCard from '../components/ProductCard';

const ShopPage: React.FC = () => {
  const { t } = useTranslation();
  const products = useProductsStore((state) => state.products);
  const categories = useProductsStore((state) => state.categories);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState<string>(searchParams.get('kategori') || 'all');
  const [sortBy, setSortBy] = useState<string>('default');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 1000]);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');

  useEffect(() => {
    const kategori = searchParams.get('kategori');
    if (kategori) setSelectedCategory(kategori);
    const q = searchParams.get('q');
    if (q) setSearchQuery(q);
  }, [searchParams]);

  const filteredProducts = useMemo(() => {
    let result: Product[] = [...products];

    // Category filter
    if (selectedCategory !== 'all') {
      result = result.filter(p => p.categories.includes(selectedCategory));
    }

    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q)
      );
    }

    // Price filter
    result = result.filter(p => {
      const price = p.salePrice > 0 ? p.salePrice : p.regularPrice;
      return price >= priceRange[0] && (priceRange[1] >= 1000 || price <= priceRange[1]);
    });

    // Sort
    switch (sortBy) {
      case 'priceLow':
        result.sort((a, b) => {
          const pa = a.salePrice > 0 ? a.salePrice : a.regularPrice;
          const pb = b.salePrice > 0 ? b.salePrice : b.regularPrice;
          return pa - pb;
        });
        break;
      case 'priceHigh':
        result.sort((a, b) => {
          const pa = a.salePrice > 0 ? a.salePrice : a.regularPrice;
          const pb = b.salePrice > 0 ? b.salePrice : b.regularPrice;
          return pb - pa;
        });
        break;
      case 'name':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
    }

    return result;
  }, [products, selectedCategory, sortBy, priceRange, searchQuery]);

  const handleCategoryChange = (slug: string) => {
    setSelectedCategory(slug);
    if (slug === 'all') {
      searchParams.delete('kategori');
    } else {
      searchParams.set('kategori', slug);
    }
    setSearchParams(searchParams);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-[#0A0A0A] to-[#1a1a2e] text-white py-10 sm:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl font-black">🚗 {t('shop.title')}</h1>
          <p className="text-gray-400 mt-2">{filteredProducts.length} {t('shop.results')}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters */}
          <aside className="lg:w-64 shrink-0">
            <div className="bg-white rounded-2xl border border-gray-100 p-5 sticky top-24 space-y-6">
              {/* Search */}
              <div>
                <h3 className="text-sm font-bold text-gray-800 mb-3 uppercase tracking-wider">🔍 {t('shop.search')}</h3>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('shop.search')}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#E31E24] transition-colors"
                />
              </div>

              {/* Categories */}
              <div>
                <h3 className="text-sm font-bold text-gray-800 mb-3 uppercase tracking-wider">📂 {t('shop.category')}</h3>
                <div className="space-y-1">
                  <button
                    onClick={() => handleCategoryChange('all')}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                      selectedCategory === 'all'
                        ? 'bg-[#E31E24] text-white font-semibold'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {t('shop.allCategories')}
                  </button>
                  {categories.filter(c => c.id !== 'kendi-setini-olustur').map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleCategoryChange(cat.slug)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                        selectedCategory === cat.slug
                          ? 'bg-[#E31E24] text-white font-semibold'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div>
                <h3 className="text-sm font-bold text-gray-800 mb-3 uppercase tracking-wider">💰 {t('shop.priceRange')}</h3>
                <input
                  type="range"
                  min={0}
                  max={1000}
                  value={priceRange[1]}
                  onChange={(e) => setPriceRange([0, parseInt(e.target.value)])}
                  className="w-full accent-[#E31E24]"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>0 ₺</span>
                  <span>{priceRange[1] >= 1000 ? '1000+ ₺' : `${priceRange[1]} ₺`}</span>
                </div>
              </div>

              {/* Sort */}
              <div>
                <h3 className="text-sm font-bold text-gray-800 mb-3 uppercase tracking-wider">📊 {t('shop.sort')}</h3>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#E31E24] transition-colors bg-white"
                >
                  <option value="default">{t('shop.sortOptions.default')}</option>
                  <option value="priceLow">{t('shop.sortOptions.priceLow')}</option>
                  <option value="priceHigh">{t('shop.sortOptions.priceHigh')}</option>
                  <option value="name">{t('shop.sortOptions.name')}</option>
                  <option value="rating">{t('shop.sortOptions.rating')}</option>
                </select>
              </div>
            </div>
          </aside>

          {/* Products Grid */}
          <div className="flex-1">
            {filteredProducts.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
                {filteredProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="text-6xl mb-4">🚗</div>
                <p className="text-gray-500 text-lg">{t('shop.noProducts')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopPage;
